import { useMemo } from "react";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useProfile } from "@/hooks/useProfile";
import { normalizeStayPlanContract } from "@/hooks/useStayPlanContracts";
import { buildStayPlanStatus } from "@/hooks/useStayPlanStatus";
import { STAY_PLAN_TABLES, type StayPlanModule } from "@/lib/daycare-hotel/stayPlans";
import type { PlanRow } from "./plansModel";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ContractRow = any;

type AdHocReservationRow = {
  id: string;
  pet_id: string;
  check_in_date: string;
  module_type: string;
  is_overnight: boolean;
  status: string;
  pets: { name: string; owners: { name: string } | null } | null;
  stay_reservation_daycare_plan_usages: Array<{ id: string }> | null;
  stay_reservation_hotel_plan_usages: Array<{ id: string }> | null;
};

export function isAdHocStayReservation(row: Pick<AdHocReservationRow, "stay_reservation_daycare_plan_usages" | "stay_reservation_hotel_plan_usages">) {
  return (row.stay_reservation_daycare_plan_usages?.length ?? 0) === 0 &&
    (row.stay_reservation_hotel_plan_usages?.length ?? 0) === 0;
}

export function buildAdHocStayRow(row: AdHocReservationRow, adHocName: string): PlanRow {
  const module = row.module_type === "hotel" ? "hotel" : "daycare";
  return {
    contractId: row.id,
    contract: {} as PlanRow["contract"],
    origin: "ad_hoc",
    petId: row.pet_id,
    petName: row.pets?.name ?? "",
    ownerName: row.pets?.owners?.name ?? "",
    planName: adHocName,
    kind: "day_package",
    module,
    type: module === "hotel" ? "hotel" : row.is_overnight ? "daycare_overnight" : "daycare",
    status: row.status,
    financialStatus: "regular",
    nextDueOn: null,
    balance: null,
    carryoverUnits: 0,
    usageRule: null,
    openChargeSaleId: null,
    paymentStatus: "none",
    openAmount: null,
    dueOn: null,
    cycleStart: null,
    cycleEnd: null,
    usedInCycle: null,
    quota: null,
    startsOn: row.check_in_date,
    usageEvents: [],
    chargeEvents: [],
  };
}

export function contractSelectWithNames(module: StayPlanModule) {
  const tables = STAY_PLAN_TABLES[module];
  return (
    "id, pet_id, owner_id, plan_id, starts_on, first_due_on, next_due_on, billing_timing, remaining_days, status, " +
    "carryover_units, carryover_expires_on, terms_snapshot, " +
    `plan:${tables.plans}(name, kind, price, ` +
    `day_package:${tables.dayPackages}(included_days), ` +
    `recurring_rule:${tables.recurringRules}(usage_rule, usage_limit)), ` +
    `usages:${tables.usages}(id, used_on, units, stay_reservation_id, source), ` +
    `charges:${tables.charges}(id, due_on, period_starts_on, period_ends_on, sale_id, sale:sales(status, total)), ` +
    "pet:pets(name), owner:owners(name)"
  );
}

export function buildPlanRow(row: ContractRow, module: StayPlanModule, today: string): PlanRow {
  const contract = normalizeStayPlanContract(row, module);
  const status = buildStayPlanStatus(contract, today);
  return {
    contractId: contract.id,
    contract,
    origin: "plan",
    petId: contract.petId,
    petName: row.pet?.name ?? "",
    ownerName: row.owner?.name ?? "",
    planName: contract.planName,
    kind: contract.kind,
    module,
    type: module,
    status: contract.status,
    financialStatus: status.financialStatus,
    nextDueOn: contract.nextDueOn,
    balance: status.balance.projected,
    carryoverUnits: contract.carryoverUnits,
    usageRule: contract.usageRule,
    openChargeSaleId: status.openChargeSaleId,
    paymentStatus: status.paymentStatus,
    openAmount: status.openAmount,
    dueOn: status.dueOn,
    cycleStart: status.cycle?.start ?? null,
    cycleEnd: status.cycle?.end ?? null,
    usedInCycle: status.usageSummary?.used ?? null,
    quota: status.usageSummary?.limit ?? null,
    startsOn: contract.startsOn,
    usageEvents: (row.usages ?? []).map((usage: ContractRow) => ({
      source: usage.source ?? "checkin",
      units: Number(usage.units ?? 0),
      usedOn: usage.used_on,
    })),
    chargeEvents: (row.charges ?? []).map((charge: ContractRow) => ({
      dueOn: charge.due_on,
      periodEnd: charge.period_ends_on,
      periodStart: charge.period_starts_on,
      saleId: charge.sale_id,
      saleStatus: charge.sale?.status ?? "",
      total: Number(charge.sale?.total ?? 0),
    })),
  };
}

/** Reservas sem qualquer uso de plano são as estadias avulsas do relatório. */
export function useAdHocStayRows() {
  const { t } = useTranslation("daycareHotel");
  const { data: profile } = useProfile();
  const clinicId = profile?.current_clinic_id ?? null;

  return useQuery<PlanRow[]>({
    queryKey: ["stay-ad-hoc-rows", clinicId],
    enabled: Boolean(clinicId),
    queryFn: async () => {
      if (!clinicId) return [];
      const { data, error } = await supabase
        .from("stay_reservations")
        .select(`id, pet_id, check_in_date, module_type, is_overnight, status,
          pets ( name, owners ( name ) ),
          stay_reservation_daycare_plan_usages ( id ),
          stay_reservation_hotel_plan_usages ( id )`)
        .eq("clinic_id", clinicId)
        .order("check_in_date", { ascending: false });
      if (error) throw error;

      return ((data ?? []) as unknown as AdHocReservationRow[])
        .filter(isAdHocStayReservation)
        .map((row) => buildAdHocStayRow(row, t("plans.report.adHocLabel")));
    },
  });
}

/** Todos os contratos de plano da clínica num módulo, com nome de pet e responsável. */
export function useStayPlanRows(module: StayPlanModule, today: string): UseQueryResult<PlanRow[]> {
  const { data: profile } = useProfile();
  const clinicId = profile?.current_clinic_id ?? null;
  const tables = STAY_PLAN_TABLES[module];

  return useQuery<PlanRow[]>({
    queryKey: ["stay-plan-rows", module, clinicId, today],
    enabled: Boolean(clinicId),
    queryFn: async () => {
      if (!clinicId) return [];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from(tables.contracts)
        .select(contractSelectWithNames(module))
        .eq("clinic_id", clinicId)
        .order("starts_on", { ascending: false });
      if (error) throw error;

      return ((data ?? []) as ContractRow[]).map((row) => buildPlanRow(row, module, today));
    },
  });
}

/** Junta creche e hotel numa lista só, para a aba Planos e o banner do painel. */
export function useAllStayPlanRows(today: string) {
  const daycare = useStayPlanRows("daycare", today);
  const hotel = useStayPlanRows("hotel", today);
  const adHoc = useAdHocStayRows();

  const rows = useMemo(() => [...(daycare.data ?? []), ...(hotel.data ?? []), ...(adHoc.data ?? [])], [adHoc.data, daycare.data, hotel.data]);

  return {
    rows,
    isLoading: daycare.isLoading || hotel.isLoading || adHoc.isLoading,
    isError: daycare.isError || hotel.isError || adHoc.isError,
    refetch: async () => Promise.all([daycare.refetch(), hotel.refetch(), adHoc.refetch()]),
  };
}
